import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import styled from "styled-components";
import {
  BlankImage,
  ArrowForwardOrange,
  LeafIcon,
} from "../../shared/icons";
import { debounce } from "lodash";
import { screenSizes } from "../../styles/theme";
import Loader from "./Loader";
import { Button } from "../../components/Buttons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { IconButton } from "../ButtonsNew";
import { getValidImageUrl } from "../../utils";

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 70%;
  position: relative;
  @media (max-width: ${screenSizes.L}px) {
    width: 100%;
    margin-bottom: 10px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid #091c001a;
  border-radius: 50px;
  font-size: 14px;
  outline: none;
  background: #f4f4f4;
  font-family: "Sora", sans-serif;
  &::placeholder {
    font-size: 16px;
    font-weight: 400;
    color: #091c0099;
  }
  @media (max-width: ${screenSizes.L}px) {
    padding: 10px;
    height: 48px;
  }
`;

const ResultsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  button {
    display: flex !important;
    align-items: center;
    justify-content: center;
    margin-inline: auto;
    height: 32px;
    width: 240px;
    margin-block: 12px;
    font-size: 16px;
    font-weight: 600;
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Rating = styled.div<{ value: number }>`
  display: flex;
  align-items: center;
  background-color: ${({ value }) =>
    value <= 3 ? "#C7FFC5" : value >= 6 ? "#FFC5C5" : "#feffc5"};
  border: 1px solid rgba(9, 28, 0, 0.1);
  padding: 0 8px;
  border-radius: 40px;
  min-width: 54px;
  justify-content: center !important;
  span {
    font-size: 14px;
    font-weight: 400;
    color: #091c00;
  }
`;

const ResultItem = styled.div`
  padding: 0px 15px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: #1e440b;
  border-bottom: 1px solid #091c001a;
  &:hover {
    background-color: #f4f4f4;
  }
  div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    img {
      height: 40px;
      width: 40px;
      border-radius: 8px;
      margin-right: 10px;
    }
    p {
      font-size: 16px;
      font-weight: 500;
    }
  }
`;

const LoaderWrapper = styled.div`
  margin: 15px 0px;
`;
const SearchInputWrapper = styled.div<{ pathname: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: ${(props) =>
    props.pathname === "/marketplace" ? "absolute" : "relative"};
  top: ${(props) => props.pathname === "/marketplace" && "88%"};
  background-color: #f1e5c0;
  padding: 2rem;
  width: 46%;
  left: ${(props) => props.pathname === "/marketplace" && "27%"};
  height: 184px;
  gap: 35px;
  margin-inline: auto;
  @media (max-width: ${screenSizes.L}px) {
    width: 90%;
    top: ${(props) => (props.pathname === "/marketplace" ? "85%" : "138%")};
    left: ${(props) => (props.pathname === "/marketplace" ? "5%" : "0")};
    margin-block: ${(props) => (props.pathname === "/marketplace" ? "0" : "20px")};
    gap: 10px;
    display: flex;
    height: ${(props) => (props.pathname === "/marketplace" ? "225px" : "255px")};
    flex-direction: column;
    padding: 1rem;
  }
  h2 {
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    text-align: left;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 20px;
      line-height: 1.5;
    }
  }
  div {
    input {
      border-color: transparent;
    }
    button {
      border-radius: 80px;
      font-family: Manrope;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      text-align: center;
      width: 160px;
      margin-top: 10px;
    }
  }
  button {
    border-radius: 80px;
    font-size: 14px;
    font-weight: 700;
    width: 100%;
    height: 48px;
    margin-top: -10px;
  }
`;
interface SearchBarProps {
  isHeaderSearch?: boolean;
  handleSearch?: (query: string) => void;
  searchResults?: any;
  isLoading?: boolean;
  placeholder?: string;
}

const AISearchBar: React.FC<SearchBarProps> = ({
  isHeaderSearch,
  handleSearch,
  searchResults,
  isLoading,
  placeholder,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const pathname = currentLocation.pathname;
  const params = useParams();
  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        handleSearch && handleSearch(query);
      }, 300),
    [handleSearch]
  );

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (event: any) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (!query.trim().length) {
      setIsDropdownVisible(false);
      debouncedSearch("");
    }
  };

  const AISearch = () => {
    debouncedSearch(searchQuery);
    setIsDropdownVisible(true);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownVisible(false);
      setSearchQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNavigation = (path: string) => {
    if (currentLocation.pathname + currentLocation.search !== path) {
      navigate(path, { replace: true });
      window.location.reload();
    } else {
      navigate(path, { replace: true });
    }
  };
  const combinedResults = searchResults?.slice(0, 5);

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const isMobile = window.innerWidth <= 1024;

  return (
    <SearchInputWrapper pathname={pathname} ref={dropdownRef}>
      <h2>Explore digital assets across chains</h2>
      <div
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <SearchContainer>
          <SearchInput
            value={searchQuery}
            onChange={handleInputChange}
            placeholder={placeholder}
          />
          {isDropdownVisible &&
            (isHeaderSearch || isLoading || combinedResults?.length > 0) && (
              <ResultsDropdown>
                {isLoading ? (
                  <LoaderWrapper>
                    <Loader />
                  </LoaderWrapper>
                ) : (
                  combinedResults?.length > 0 && (
                    <>
                      {combinedResults?.map((item: any, index: number) => {
                        return (
                          <ResultItem
                            key={`result-${index}`}
                            onClick={() =>{
                              if(item?.id === params.id){
                                setIsDropdownVisible(false);
                                window.scrollTo(0, 0);
                                setSearchQuery("");
                              } else {
                                handleNavigation(`/${item.type}-details/${item?.id}`)
                              }
                            }
                          }
                          >
                            <div>
                              <div>
                              <img
                                src={
                                  getValidImageUrl(item?.imageUrl,BlankImage )
                                }
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = BlankImage;
                                }}
                                alt="img"
                              />
                              <p>{item.name}</p>
                              </div>
                              <Rating value={item?.score}>
                                <LeafIcon />
                                <span>
                                  {typeof item?.score === "number"
                                    ? item.score.toFixed(1)
                                    : "0"}
                                </span>
                              </Rating>
                            </div>
                          </ResultItem>
                        );
                      })}
                      <IconButton
                        icon={<ArrowForwardOrange />}
                        onClick={() => {
                          if (searchQuery.length) {
                            navigate(`/search-results/${searchQuery}`);
                          } else {
                            navigate("/search-results");
                          }
                          setSearchQuery("");
                          setIsDropdownVisible(false);
                        }}
                      >
                        View All
                      </IconButton>
                    </>
                  )
                )}
              </ResultsDropdown>
            )}
        </SearchContainer>
        {!isMobile && (
          <Button variant="secondary" onClick={() => AISearch()}>
            AI Search
          </Button>
        )}
      </div>
      {isMobile && (
        <Button variant="secondary" onClick={() => AISearch()}>
          AI Search
        </Button>
      )}
    </SearchInputWrapper>
  );
};

export default AISearchBar;
